import { isNamedVersion } from './utils/namedVersionUtils';
import { isLocalhostEnvironment } from './utils/utils';
// SINGLETON ALERT!
const CACHED_CONFIG_FLAG_STATES = {};
// SINGLETON ALERT!
let CACHED_OVERRIDDEN_CONFIG_FLAG_STATES;
const ALL_CONFIG_FLAGS = [
    'allInGames',
    'scrollLocking',
    /**
     * Disabled showing of 'RECONNECT' badge on lobby room until it can be dynamically updated based on the player leaving the room
     * in another session, or a room being created / joined from another session.
     * TODO: Add server support for this and enable.
     */
    'reconnectBadgeOnLobbyRoom',
    'limitMaxNumSeats',
    'clientSideEmojiReplacement',
    'twemoji',
    'hiDpi',
    'setBetButtons',
    'sideBets',
    'disableAntialiasing',
    'simpleBetting',
    /**
     * Useful for getting the simple betting context from the browser console for unit tests and debugging.
     */
    'simpleBettingDataDebugLog',
    'adminPanel',
    'tourneyWinSharing',
    'chipsAddedAnimation',
    'staggerAppInstanceLoads',
    'playerQuickActions',
    'powerPlayerOnlyGamesMustBeCreatedBySuperAdmin',
    'domBetButtons',
    'onlyAllowCallOnInsufficientRaises',
    'useParallelAvatarServiceRender',
    'uniqueAvatarsCompetition',
    'dualLimitGameSetting',
    'showCommunityModal',
    'showDiscordButton',
    'panelAnimations',
    'animatedRoomBackgroundsAvailable',
    'animatedRoomBackgroundChangesDirection',
    'preloadSoundsWithHeadTags',
    'loadBombsInBackgroundInParallel',
    'columnLayoutMode',
    'allowColumnLayoutModeOnDesktop',
    'namedVersionDevHeader',
    'tableShakeAnimations',
    'expiringTimeTableShakeAnimation',
    'expiringTimePlayerShakeAnimation',
    'showChipsAsBbMultipleAvailable',
    'goToPathAvailableForAllUsers',
    'contactUsModal',
    'smallerAvatarTextures',
    'autoReconnect',
    /**
     * Whether to use container queries to scale down panel contents based on panel size.
     */
    'panelContainerQueries'
];
export const ALL_CONFIG_FLAG_PROPERTIES = {
    allInGames: {
        isEnabled: true
    },
    scrollLocking: {
        isEnabled: false
    },
    reconnectBadgeOnLobbyRoom: {
        isEnabled: false
    },
    limitMaxNumSeats: {
        isEnabled: false
    },
    clientSideEmojiReplacement: {
        isEnabled: true
    },
    twemoji: {
        isEnabled: true
    },
    hiDpi: {
        isEnabled: true,
        overrides: { overridableStates: false }
    },
    setBetButtons: {
        isEnabled: false,
        overrides: { overridableStates: true }
    },
    sideBets: {
        isEnabled: true
    },
    disableAntialiasing: {
        isEnabled: false,
        overrides: { overridableStates: true }
    },
    simpleBetting: {
        isEnabled: false,
        overrides: { overridableStates: true }
    },
    simpleBettingDataDebugLog: {
        isEnabled: false
    },
    adminPanel: {
        isEnabled: true
    },
    tourneyWinSharing: {
        isEnabled: true
    },
    chipsAddedAnimation: {
        isEnabled: false
    },
    staggerAppInstanceLoads: {
        isEnabled: 'localhost'
    },
    playerQuickActions: {
        isEnabled: false
    },
    powerPlayerOnlyGamesMustBeCreatedBySuperAdmin: {
        isEnabled: 'live'
    },
    domBetButtons: {
        name: 'New Bet Buttons',
        isEnabled: true
    },
    onlyAllowCallOnInsufficientRaises: {
        isEnabled: false
    },
    useParallelAvatarServiceRender: {
        isEnabled: false
    },
    uniqueAvatarsCompetition: {
        isEnabled: false
    },
    dualLimitGameSetting: {
        isEnabled: true
    },
    showCommunityModal: {
        isEnabled: false
    },
    showDiscordButton: {
        isEnabled: false
    },
    panelAnimations: {
        isEnabled: true
    },
    animatedRoomBackgroundsAvailable: {
        isEnabled: 'localhost'
    },
    animatedRoomBackgroundChangesDirection: {
        isEnabled: false
    },
    preloadSoundsWithHeadTags: {
        isEnabled: false
    },
    loadBombsInBackgroundInParallel: {
        isEnabled: false
    },
    columnLayoutMode: {
        isEnabled: () => isLocalhostEnvironment() || isNamedVersion()
    },
    allowColumnLayoutModeOnDesktop: {
        isEnabled: true
    },
    namedVersionDevHeader: {
        isEnabled: true
    },
    tableShakeAnimations: {
        isEnabled: 'localhost'
    },
    expiringTimeTableShakeAnimation: {
        isEnabled: false
    },
    expiringTimePlayerShakeAnimation: {
        isEnabled: false
    },
    showChipsAsBbMultipleAvailable: {
        isEnabled: true
    },
    goToPathAvailableForAllUsers: {
        isEnabled: true
    },
    contactUsModal: {
        isEnabled: true
    },
    smallerAvatarTextures: {
        isEnabled: true
    },
    autoReconnect: {
        isEnabled: false
    },
    panelContainerQueries: {
        isEnabled: true,
        overrides: { overridableStates: false }
    }
};
function isConfigFlagEnabledByDefault({ isEnabled }) {
    if (isEnabled === 'localhost') {
        return isLocalhostEnvironment();
    }
    if (isEnabled === 'live') {
        return !isLocalhostEnvironment();
    }
    if (typeof isEnabled === 'function') {
        return isEnabled();
    }
    // otherwise, it's a boolean constant
    return isEnabled;
}
function calcIsConfigFlagEnabled(configFlag) {
    const configFlagProperties = ALL_CONFIG_FLAG_PROPERTIES[configFlag];
    const isEnabledByDefault = isConfigFlagEnabledByDefault(configFlagProperties);
    // check if the flag is overridden in the URL:
    if (configFlagProperties.overrides) {
        const params = new URLSearchParams(window.location.search);
        if (params.get(configFlag) === 'on' &&
            (configFlagProperties.overrides.overridableStates === true ||
                (Array.isArray(configFlagProperties.overrides.overridableStates) &&
                    configFlagProperties.overrides.overridableStates.includes(true)))) {
            // allowed to override to true, and is overriding to true
            return true;
        }
        if (params.get(configFlag) === 'off' &&
            (configFlagProperties.overrides.overridableStates === false ||
                (Array.isArray(configFlagProperties.overrides.overridableStates) &&
                    configFlagProperties.overrides.overridableStates.includes(false)))) {
            // allowed to override to false, and is overriding to false
            return false;
        }
    }
    return isEnabledByDefault;
}
export function isConfigFlagEnabled(configFlag) {
    const cachedConfigFlagState = CACHED_CONFIG_FLAG_STATES[configFlag];
    if (cachedConfigFlagState !== undefined) {
        // this config flag has already been calculated and stored, so just return it
        return cachedConfigFlagState;
    }
    const isEnabled = calcIsConfigFlagEnabled(configFlag);
    // cache it:
    CACHED_CONFIG_FLAG_STATES[configFlag] = isEnabled;
    return isEnabled;
}
/**
 * Returns a true/false value only for config flag values that differ from their defaults in this environment.
 */
function calcOverriddenConfigFlagStates() {
    const overriddenConfigFlags = {};
    ALL_CONFIG_FLAGS.forEach((configFlag) => {
        const configFlagProperties = ALL_CONFIG_FLAG_PROPERTIES[configFlag];
        const isEnabledByDefault = isConfigFlagEnabledByDefault(configFlagProperties);
        const isEnabled = isConfigFlagEnabled(configFlag);
        if (isEnabled !== isEnabledByDefault) {
            overriddenConfigFlags[configFlag] = isEnabled;
        }
    });
    return overriddenConfigFlags;
}
export function getOverriddenConfigFlagStates() {
    if (!CACHED_OVERRIDDEN_CONFIG_FLAG_STATES) {
        CACHED_OVERRIDDEN_CONFIG_FLAG_STATES = calcOverriddenConfigFlagStates();
    }
    return CACHED_OVERRIDDEN_CONFIG_FLAG_STATES;
}
export function getSettingsOverridableConfigFlags() {
    return ALL_CONFIG_FLAGS.filter((configFlag) => ALL_CONFIG_FLAG_PROPERTIES[configFlag].overrides?.canBeOverriddenInSettings);
}
export function overrideConfigFlag(configFlag, isEnabled) {
    CACHED_CONFIG_FLAG_STATES[configFlag] = isEnabled;
    CACHED_OVERRIDDEN_CONFIG_FLAG_STATES = calcOverriddenConfigFlagStates();
}
